import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, UserCheck, ArrowRight, BadgeCheck, AlertCircle, Settings, Coins, KeyRound, Lock, ListChecks } from "lucide-react";
import { apiMembership, apiBillingPortal, apiSetPassword, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await apiMembership();
        if (alive) setM(data);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  const active = !!m?.active;
  const cancelPending = !!m?.cancel_at_period_end;

  const openPortal = async () => {
    try {
      const { url } = await apiBillingPortal(window.location.origin + "/dashboard");
      window.location.href = url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="pt-12 md:pt-16 max-w-4xl mx-auto pb-20" data-testid="dashboard-page">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
          ── member dashboard ──
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white">
          Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-sm text-neutral-400 mt-1.5">{user?.email}</p>

        {/* Membership status */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            testid="card-status"
            label="Membership"
            value={active ? (cancelPending ? "Cancelling" : "Active") : "Inactive"}
            sub={
              active
                ? (cancelPending ? "Ends on next renewal" : "Auto-renews monthly")
                : "Become a Member to unlock"
            }
            icon={active ? <BadgeCheck className="w-5 h-5" style={{ color: cancelPending ? "#FFB000" : "#00FF66" }} />
                          : <AlertCircle className="w-5 h-5 text-amber-400" />}
            accent={active ? (cancelPending ? "#FFB000" : "#00FF66") : "#FFB000"}
          />
          <StatCard
            testid="card-reviews"
            label="Expert Reviews"
            value={m ? `${m.reviews_remaining ?? 0} / 2` : "—"}
            sub={
              (m?.extra_reviews ?? 0) > 0
                ? `+${m.extra_reviews} extra review${m.extra_reviews === 1 ? "" : "s"} available`
                : "2 included each month · no rollover"
            }
          />
          <StatCard
            testid="card-renewal"
            label="Next renewal"
            value={fmtDate(m?.next_renewal_at)}
            sub={active ? "Reviews reset each cycle" : "Activate to set renewal"}
          />
        </div>

        {err && <div className="mt-4 text-xs text-red-400" data-testid="dashboard-error">{err}</div>}

        {/* CTAs — two distinct entry points */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ActionCard
            testid="goto-market"
            disabled={!active}
            onClick={() => navigate(active ? "/market" : "/membership")}
            icon={<Activity className="w-5 h-5" />}
            title="Live Market Analysis"
            sub={active ? "Live Binance data. Chart-first analysis." : "Members only — unlock below."}
            cta="Open Market"
            accent="#00E5FF"
          />
          <ActionCard
            testid="goto-expert"
            disabled={!active}
            onClick={() => navigate(active ? "/expert-review" : "/membership")}
            icon={<UserCheck className="w-5 h-5" />}
            title="Expert Review"
            sub={active ? "Human trader across timeframes." : "Members only — unlock below."}
            cta="Request Review"
            accent="#FFB000"
            secondary={{
              label: "My Expert Reviews",
              testid: "goto-my-expert-reviews",
              onClick: () => navigate(active ? "/expert-review#your-requests" : "/membership"),
            }}
          />
        </div>

        {!active && (
          <div className="mt-8">
            <Link
              to="/membership"
              data-testid="dashboard-membership-cta"
              className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors"
            >
              Become a Member — €9.99/mo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {m?.portal_available && (
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={openPortal}
              data-testid="manage-subscription-btn"
              className="inline-flex items-center gap-2 border border-neutral-800 hover:border-neutral-600 text-neutral-300 hover:text-white font-mono uppercase tracking-[0.2em] text-xs px-4 py-2.5 transition-colors"
            >
              <Settings className="w-3.5 h-3.5" /> manage subscription
            </button>
            <button
              onClick={() => navigate("/buy-credits")}
              data-testid="dashboard-buy-credits"
              className="inline-flex items-center gap-2 border border-neutral-800 hover:border-[#FFB000] hover:text-[#FFB000] text-neutral-300 font-mono uppercase tracking-[0.2em] text-xs px-4 py-2.5 transition-colors"
            >
              <Coins className="w-3.5 h-3.5" /> buy extra reviews
            </button>
          </div>
        )}

        {/* Account password — add (Google-only) or change (existing pw) */}
        <PasswordSection />
      </motion.div>
    </section>
  );
}

function PasswordSection() {
  const { user, refresh } = useAuth();
  const hasPassword = !!user?.password_set;
  const [open, setOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const reset = () => {
    setCurrentPw(""); setNewPw(""); setConfirmPw(""); setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await apiSetPassword({
        new_password: newPw,
        current_password: hasPassword ? currentPw : undefined,
      });
      await refresh();
      toast.success(hasPassword ? "Password updated." : "Password added — you can now log in with email & password.");
      reset();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 border border-neutral-900 bg-neutral-950 p-6" data-testid="password-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="text-[#00E5FF] mt-1"><KeyRound className="w-5 h-5" /></span>
          <div>
            <div className="font-bold text-white">Account password</div>
            <div className="text-xs text-neutral-400 mt-1 max-w-md">
              {hasPassword
                ? "You can log in with email & password OR Google. Change your password anytime."
                : "You currently sign in with Google. Add a password to log in with email too — Google sign-in keeps working."}
            </div>
          </div>
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            data-testid={hasPassword ? "change-password-btn" : "add-password-btn"}
            className="inline-flex items-center gap-2 border border-neutral-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-neutral-300 font-mono uppercase tracking-[0.2em] text-xs px-4 py-2.5 transition-colors"
          >
            <Lock className="w-3.5 h-3.5" /> {hasPassword ? "change password" : "add password"}
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-5 grid gap-3 max-w-sm" data-testid="password-form">
          {hasPassword && (
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Current password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              data-testid="password-current"
              className="bg-black border border-neutral-800 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-[#00E5FF]"
              required
            />
          )}
          <input
            type="password"
            autoComplete="new-password"
            placeholder={hasPassword ? "New password (min 6 chars)" : "Password (min 6 chars)"}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            data-testid="password-new"
            className="bg-black border border-neutral-800 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-[#00E5FF]"
            minLength={6}
            required
          />
          <input
            type="password"
            autoComplete="new-password"
            placeholder="Confirm password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            data-testid="password-confirm"
            className="bg-black border border-neutral-800 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-[#00E5FF]"
            minLength={6}
            required
          />
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="password-save"
              className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-5 py-2.5 hover:bg-neutral-200 transition-colors disabled:opacity-40"
            >
              {busy ? "Saving…" : (hasPassword ? "Save password" : "Add password")}
            </button>
            <button
              type="button"
              onClick={reset}
              data-testid="password-cancel"
              className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-300 px-3 py-2.5"
            >
              cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon, accent = "#FFFFFF", testid }) {
  return (
    <div className="border border-neutral-900 bg-neutral-950 p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">{label}</div>
        {icon}
      </div>
      <div className="mt-3 font-black text-3xl tracking-tight" style={{ color: accent }}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1.5">{sub}</div>
    </div>
  );
}

function ActionCard({ icon, title, sub, cta, accent, onClick, disabled, testid, secondary }) {
  return (
    <div className="border border-neutral-900 bg-neutral-950 p-6 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <span style={{ color: accent }}>{icon}</span>
        <div className="font-bold text-white">{title}</div>
      </div>
      <div className="text-sm text-neutral-400 mt-2 flex-1">{sub}</div>
      <button
        onClick={onClick}
        disabled={disabled}
        className="mt-4 inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-5 py-2.5 hover:bg-neutral-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {cta} <ArrowRight className="w-4 h-4" />
      </button>
      {secondary && (
        <button
          onClick={secondary.onClick}
          disabled={disabled}
          data-testid={secondary.testid}
          className="mt-2 inline-flex items-center justify-center gap-2 border border-neutral-800 hover:border-[#FFB000] hover:text-[#FFB000] text-neutral-300 font-mono uppercase tracking-[0.2em] text-xs px-5 py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ListChecks className="w-3.5 h-3.5" /> {secondary.label}
        </button>
      )}
    </div>
  );
}
